import { formatDate } from "@common/lib/date-utils"
import { z } from "zod"

export const periodSchema = z.enum([
  "last_month",
  "last_3_months",
  "last_year",
  "custom",
])
export type Period = z.infer<typeof periodSchema>
export type PeriodPreset = Exclude<Period, "custom">

export const PERIOD_PRESETS: { value: PeriodPreset; label: string }[] = [
  { value: "last_month", label: "Last month" },
  { value: "last_3_months", label: "Last 3 months" },
  { value: "last_year", label: "Last year" },
]

export function localIso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${date.getFullYear()}-${month}-${day}`
}

export function parseIso(iso: string): Date {
  const [year, month, day] = iso.split("-").map(Number)
  return new Date(year, month - 1, day)
}

export function periodRange(
  period: Period | undefined,
  from: string | undefined,
  to: string | undefined,
): { from: string | null; to: string | null } {
  if (!period) return { from: null, to: null }
  if (period === "custom") return { from: from ?? null, to: to ?? null }

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const start = new Date(today)
  switch (period) {
    case "last_month":
      start.setMonth(start.getMonth() - 1)
      break
    case "last_3_months":
      start.setMonth(start.getMonth() - 3)
      break
    case "last_year":
      start.setFullYear(start.getFullYear() - 1)
      break
  }
  return { from: localIso(start), to: localIso(today) }
}

export function periodLabel(
  period: Period,
  from?: string,
  to?: string,
): string {
  if (period === "custom") {
    if (from && to) return `${formatDate(from)} – ${formatDate(to)}`
    return "Custom"
  }
  return PERIOD_PRESETS.find((preset) => preset.value === period)?.label ?? ""
}
